import { RootState } from 'app/store';
import { createSelector } from 'reselect';
import { OTHER_CATEGORY_ID } from '../categories';
import { getCategoryName } from '../categories.utils';
import categoriesAdapter from './categories.adapter';
import { Category, CategoryGroup, SelectableCategories } from './types';

export const {
  selectAll: selectCategories,
  selectById: selectCategoryById,
  selectIds: selectCategoriesIds,
  selectEntities: selectCategoriesEntities,
} = categoriesAdapter.getSelectors((state: RootState) => state.categories);

export const selectCategoryName = createSelector(
  [selectCategoriesEntities, (state: RootState, categoryId?: string) => categoryId],
  (entities, categoryId) => {
    const category = categoryId ? entities[categoryId] : null;

    return category ? getCategoryName(category) : '-';
  },
);

export const selectCategoryIcon = createSelector(
  [selectCategoriesEntities, (state: RootState, categoryId?: string) => categoryId],
  (entities, categoryId) => {
    const category = categoryId ? entities[categoryId] : null;
    return category ? category.icon : null;
  },
);

const byOrder = (a: { order: number | null }, b: { order: number | null }) => (a.order ?? 0) - (b.order ?? 0);

export const selectCategoryGroups = createSelector(
  (state: RootState): CategoryGroup[] => state.categories.groups,
  groups => [...groups].sort(byOrder),
);

export const selectSelectableCategoriesByGroup = createSelector(
  [selectCategories, selectCategoryGroups],
  (categories, groups): SelectableCategories => {
    const active = categories.filter((category: Category) => !category.retired);

    return {
      groups: groups.map(group => ({
        group,
        categories: active.filter(category => category.groupId === group.id).sort(byOrder),
      })),
      other: active.find(category => category.id === OTHER_CATEGORY_ID) ?? null,
    };
  },
);

export const selectRetiredCategories = createSelector(selectCategories, categories =>
  categories.filter((category: Category) => category.retired).sort((a, b) => a.name_fr.localeCompare(b.name_fr, 'fr')),
);
